import { routes } from '@app/constant';
import {
    getOutdatedComputers,
    getOutdatedKey,
    LauncherUpdateNotice,
} from '@components/LauncherUpdateNotice/LauncherUpdateNotice';
import { StorageKey } from '@shared/enum';
import { localStorageService } from '@shared/localStorageService';
import { useGetComputersQuery } from '@state/assignmentApi';
import { useAppSelector } from '@state/configureStore';
import { selectIsAuthenticated, selectLanguage, selectUserRole } from '@state/pageSlice';
import { UserRole } from '@state/userApi';
import type { FC } from 'react';
import { useState } from 'react';
import { Container } from 'react-bootstrap';
import { matchPath, useLocation } from 'react-router-dom';

// Launchers only change version on restart, so a slow poll is enough here. On the
// assignment page the same query (same args) polls every 10s and shares this cache.
const POLL_MS = 5 * 60 * 1000;

/**
 * Site-wide notice for instructors/admins when a simulator in their company runs an
 * outdated launcher. Dismissible; it reappears when the set of outdated simulators or
 * the latest version changes. Hidden on the assignment page, which shows its own.
 */
export const LauncherUpdateBanner: FC = () => {
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const userRole = useAppSelector(selectUserRole);
    const language = useAppSelector(selectLanguage);
    const location = useLocation();
    const [dismissedKey, setDismissedKey] = useState<string | null>(() =>
        localStorageService.getItem<string>(StorageKey.launcherUpdateDismissed),
    );

    const canManageSimulators = userRole === UserRole.INSTRUCTOR || userRole === UserRole.SYSTEM_ADMINISTRATOR;
    const onAssignmentManage = matchPath(routes.assignmentManage, location.pathname) !== null;

    // Errors (e.g. 400 for a company without simulators) are deliberately not surfaced.
    const { data } = useGetComputersQuery(
        { language, return_all: true },
        { skip: !isAuthenticated || !canManageSimulators, pollingInterval: POLL_MS },
    );

    if (!isAuthenticated || !canManageSimulators || onAssignmentManage) {
        return null;
    }

    const outdated = getOutdatedComputers(data?.data);
    if (outdated.length === 0) {
        return null;
    }
    const key = getOutdatedKey(outdated);
    if (key === dismissedKey) {
        return null;
    }

    const dismiss = () => {
        localStorageService.setItem(StorageKey.launcherUpdateDismissed, key);
        setDismissedKey(key);
    };

    return (
        <Container fluid="xxl" className="pt-3">
            <LauncherUpdateNotice computers={outdated} onClose={dismiss} className="mb-0" />
        </Container>
    );
};
